import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Supabase yapılandırılmış mı kontrol et
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey &&
  supabaseUrl !== 'your-supabase-url-here' &&
  supabaseAnonKey !== 'your-supabase-anon-key-here');

// Supabase client - sadece yapılandırılmışsa oluştur
let supabaseClient: SupabaseClient | null = null;

if (isSupabaseConfigured) {
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
} else {
  console.warn('⚠️ Supabase yapılandırılmamış. .env dosyasındaki REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY değerlerini doldurun.');
}

export const supabase = supabaseClient;

// Kullanıcı rolleri
export type UserRole = 'personel' | 'mudur' | 'admin';

// Kurum tipi (Veritabanı tablosu ile eşleşmeli)
export interface Institution {
  id: string;
  name: string;
  logo_url?: string;
  is_active: boolean;
  created_at?: string;
}

// Simülasyon tipi
export interface Simulation {
  id: string;
  institution_id: string;
  title: string;
  description?: string;
  config: Record<string, any>; // JSONB config
  is_active: boolean;
  created_at?: string;
}

// Kullanıcı tipi
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  institution_id?: string;
  avatar_url?: string;
  created_at: string;
  institution_details?: Institution;
}



// Giriş yapma fonksiyonu (kurum kontrolü kaldırıldı)
export async function signIn(email: string, password: string): Promise<{ user: User | null; error: string | null }> {
  if (!supabase) {
    return { user: null, error: 'Supabase yapılandırılmamış. Lütfen .env dosyasını kontrol edin.' };
  }

  try {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      return { user: null, error: authError.message };
    }

    if (!authData.user) {
      return { user: null, error: 'Kullanıcı bulunamadı' };
    }

    // Kullanıcı profilini çek
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*, institutions:institution_id(*)')
      .eq('id', authData.user.id)
      .single();

    if (profileError) {
      return { user: null, error: 'Kullanıcı profili bulunamadı' };
    }

    const userData: User = {
      id: authData.user.id,
      email: authData.user.email || '',
      full_name: profile.full_name,
      role: profile.role as UserRole,
      institution_id: profile.institution_id,
      institution_details: profile.institutions,
      avatar_url: profile.avatar_url,
      created_at: profile.created_at,
    };

    return {
      user: userData,
      error: null,
    };
  } catch (error: any) {
    return { user: null, error: error.message || 'Bir hata oluştu' };
  }
}

// Çıkış yapma fonksiyonu
export async function signOut(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}

// Mevcut oturumu kontrol et
export async function getCurrentUser(): Promise<User | null> {
  // Supabase yapılandırılmamışsa null döndür
  if (!supabase) {
    return null;
  }

  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();

    if (!authUser) {
      return null;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*, institutions:institution_id(*)')
      .eq('id', authUser.id)
      .single();

    if (error || !profile) {
      return null;
    }

    return {
      id: authUser.id,
      email: authUser.email || '',
      full_name: profile.full_name,
      role: profile.role as UserRole,
      institution_id: profile.institution_id,
      institution_details: profile.institutions,
      avatar_url: profile.avatar_url,
      created_at: profile.created_at,
    };
  } catch {
    return null;
  }
}
// Simülasyon Raporu
export interface SimulationReport {
  id: string;
  user_id: string;
  simulation_id: string;
  simulation_title?: string;
  created_at: string;
  report_data: any; // ReportData tipini burada tekrar tanımlamak yerine any veya import kullanıyoruz
  score?: number;
}

// Modül Adım Tipleri
export interface QuizItem {
  type: 'quiz';
  question: string;
  options: string[];
  correctIndex: number;
}

export interface MiniSimItem {
  type: 'mini_sim';
  avatarUrl: string;
  voice: string;
  aiQuestion: string;
  expectedAnswer: string;
}

export interface MainSimItem {
  type: 'main_sim';
  avatarUrl: string;
  voice: string;
  missions: string[];
  systemInstruction: string;
  userInstruction: string;
}

export type ModuleItem = QuizItem | MiniSimItem | MainSimItem;

// Modül tipi
export interface Module {
  id: string;
  institution_id: string;
  title: string;
  description?: string;
  items: ModuleItem[];
  is_active: boolean;
  created_at?: string;
}
